import { Fragment, useMemo } from "react";
import { PropTypes } from "prop-types";
import Accordion from "react-bootstrap/Accordion";
import { MathJax } from "better-react-mathjax";

import RatingBlock from "./RatingBlock";
import T from "../translations/Translations";

import { getScrubbedRating, isParentFacingRating } from "../../utils/teacher/InputRatingUtils";
import {
    mergeSubjectStandards,
    mergedStandardsHaveParentFacingRatings,
    subjectHasParentFacingCustomData
} from "../../utils/guardianVisibleSubjects";
import { gradeTranslation } from "../../utils/SchoolAdmin/translations";

const compareText = (a, b) => (a ?? "").localeCompare(b ?? "");

const API_GRADE_TO_NUMBER = {
    K: 0,
    FIRST: 1,
    SECOND: 2,
    THIRD: 3,
    FOURTH: 4,
    FIFTH: 5,
    SIXTH: 6,
    SEVENTH: 7,
    EIGHTH: 8
};

const gradeStringToNumber = (gradeStr) => {
    if (gradeStr == null || gradeStr === "") {
        return Infinity;
    }
    const raw = gradeStr.toString().trim();
    const upper = raw.toUpperCase();
    if (upper.startsWith("K")) {
        return 0;
    }
    if (API_GRADE_TO_NUMBER[upper] != null) {
        return API_GRADE_TO_NUMBER[upper];
    }
    const fromDisplay = Object.entries(gradeTranslation).find(([, label]) => String(label).toUpperCase() === upper);
    if (fromDisplay) {
        return API_GRADE_TO_NUMBER[fromDisplay[0]] ?? Infinity;
    }
    const match = raw.match(/^(\d+)/);
    return match ? Number.parseInt(match[1], 10) : Infinity;
};

const getGradeHeader = (gradeNumber) => {
    if (gradeNumber === 0) {
        return "Kindergarten";
    }
    const grade = gradeStringToNumber(gradeNumber);
    switch (grade) {
        case 1:
            return "1st Grade";
        case 2:
            return "2nd Grade";
        case 3:
            return "3rd Grade";
        default:
            return `${grade}th Grade`;
    }
};

/**
 * Checks whether a GLE has a direct content rating (i.e. the GLE itself is the
 * reportable item, not its evidence outcomes). This is the case for specials
 * subjects (Art, Music, PE) where the rating is attached at the GLE level.
 * We detect this by checking for a contentRating directly on the gle object itself
 * (not on a child EO), which the backend sets when contentType is "GLE".
 */
const gleHasDirectRating = (gle) => {
    if (!gle) return false;
    return isParentFacingRating(gle.contentRating?.rating);
};

/**
 * Extracts the direct GLE-level contentRating from gle.contentRating.
 */
const getGleDirectRating = (gle) => {
    return gle?.contentRating ?? null;
};

const eoDiffRatingIsRated = (eo) => {
    const hasDirectDiffRating = isParentFacingRating(eo.diffRating?.rating);
    const hasChildDiffRating = eo.evidenceOutcomeChildren?.some((child) =>
        isParentFacingRating(child.diffRating?.rating)
    );
    return hasDirectDiffRating || hasChildDiffRating;
};

/**
 * Checks a collection of standards to see if any evidence outcome,
 * or any of its children, has a differential rating block ('diffRating').
 * @name getHasDiffItems
 * @param {Array<Object>} standards - The array of standard objects.
 * @returns {boolean} True if a diffRating is found at any level, false otherwise.
 */
const gleHasDiffItems = (gle) => {
    if (isParentFacingRating(gle.diffRating?.rating)) return true;
    return gle.evidenceOutcomes?.some(eoDiffRatingIsRated) ?? false;
};

const getHasDiffItems = (standards) => {
    return standards.some((standard) => standard.gles?.some(gleHasDiffItems));
};

/**
 * Determine if the object or its children contains any rated content.
 * @name hasRatedElements
 * @param {Array} eos
 * @returns {boolean}
 */
const hasRatedElements = (eos) => {
    if (!eos || eos.length < 1) return false;

    for (const eo of eos) {
        const { contentRating, diffRating, evidenceOutcomeChildren } = eo;
        if (isParentFacingRating(contentRating?.rating)) return true;
        if (isParentFacingRating(diffRating?.rating)) return true;
        if (evidenceOutcomeChildren?.length > 0 && hasRatedElements(evidenceOutcomeChildren)) return true;
    }

    return false;
};

/**
 * Body-less EOs are GLE-level ratings (content or differentiated).
 */
const getGleRatingEo = (gle) =>
    gle?.evidenceOutcomes?.find((eo) => !eo.eoBody && (eo.contentRating || eo.diffRating)) ?? null;

/**
 * Returns true if a GLE has any rated content (direct GLE rating,
 * pseudo-EO GLE rating, or real EO ratings).
 */
const gleGroupIsRated = (gle) => {
    if (gleHasDirectRating(gle)) return true;
    if (isParentFacingRating(gle.diffRating?.rating)) return true;
    return hasRatedElements(gle.evidenceOutcomes);
};

/**
 * @description Check to see if there are ratings under the standard
 * @name standardHasRatedElements
 * @param {{}} standard
 * @returns {bool}
 */
const standardHasRatedElements = (standard) => {
    const { gles } = standard ?? {};
    if (!gles) return false;
    return gles.some(gleGroupIsRated);
};

/**
 * Collects all items with a rated diffRating from an EO and its children.
 */
const collectDiffRatedItems = (eo) => {
    const items = [];
    if (isParentFacingRating(eo.diffRating?.rating)) {
        items.push(eo);
    }
    eo.evidenceOutcomeChildren?.forEach((child) => {
        if (isParentFacingRating(child.diffRating?.rating)) {
            items.push(child);
        }
    });
    return items;
};

/**
 * Finds the lowest numeric grade from any 'diffRating' block
 * within a GLE's evidence outcomes OR their children.
 * @name getLowestDiffGradeNumber
 * @param {Object} gle - The Grade Level Expectation object.
 * @returns {number | null} The lowest numeric grade, or null if no diffRating is found.
 */
const getLowestDiffGradeNumber = (gle) => {
    const fromGle = isParentFacingRating(gle.diffRating?.rating) ? [gle] : [];
    const allDifferentiatedItems = [...fromGle, ...(gle.evidenceOutcomes ?? []).flatMap(collectDiffRatedItems)];

    if (allDifferentiatedItems.length === 0) {
        return null;
    }

    const numericGrades = allDifferentiatedItems
        .map((item) => item.diffRating?.grade)
        .filter((grade) => grade != null && grade !== "")
        .map(gradeStringToNumber);
    if (numericGrades.length === 0) {
        return null;
    }
    return Math.min(...numericGrades);
};

/**
 * Core comparator function implementing the final 3-tiered sorting logic based on a dynamic default grade.
 *
 * @param {Object} gleA
 * @param {Object} gleB
 * @param {number} defaultGradeNum The numeric representation of the current student's default grade.
 * @returns {number} Standard comparison return value (-1, 0, 1).
 */
const gleComparatorDynamic = (gleA, gleB, defaultGradeNum) => {
    const gradeA = getLowestDiffGradeNumber(gleA);
    const gradeB = getLowestDiffGradeNumber(gleB);
    const isDifferentiatedA = gradeA !== null;
    const isDifferentiatedB = gradeB !== null;

    // --- Step 1: Assign Priority Group ---
    const getPriority = (isDiff, grade) => {
        // Priority 1 (Highest): Differentiated and matches the dynamic default grade.
        if (isDiff && grade === defaultGradeNum) {
            return 1;
        }
        // Priority 2: Non-Differentiated.
        if (!isDiff) {
            return 2;
        }
        // Priority 3: All other Differentiated content (higher or lower than default).
        return 3;
    };

    const priorityA = getPriority(isDifferentiatedA, gradeA);
    const priorityB = getPriority(isDifferentiatedB, gradeB);

    // If priorities differ, sort by priority number (lower number comes first).
    if (priorityA !== priorityB) {
        return priorityA - priorityB;
    }

    // --- Step 2: Tie-Breakers (Same Priority Group) ---

    // P1 (Differentiated @ Default Grade) and P2 (Non-Differentiated) both sort by name.
    if (priorityA === 1 || priorityA === 2) {
        // Sort internally by GLE name (gleBody)
        return compareText(gleA.gleBody, gleB.gleBody);
    }

    // P3 (All other Differentiated content)
    if (priorityA === 3) {
        // Sort internally by Grade: Highest Grade differentiation to Lowest Grade differentiation.
        // gradeB - gradeA achieves descending numeric sort
        return gradeB - gradeA;
    }

    return 0;
};

/**
 * Defines the unique key for grouping, which respects the three priority tiers
 * and separates P3 items by their actual differentiated grade.
 */
const getUniqueGroupKey = (gle, defaultGradeNum, gradeName) => {
    const grade = getLowestDiffGradeNumber(gle);
    const isDiff = grade !== null;

    const subjectAccelerated = T({ key: "Subject_Accelerated" });

    if (isDiff && grade === defaultGradeNum) {
        return `${getGradeHeader(grade)} - ${subjectAccelerated}`;
    }
    if (!isDiff) {
        return getGradeHeader(gradeName);
    }
    // P3: differentiated at a grade other than the default — group by that grade
    return `${getGradeHeader(grade)} - ${subjectAccelerated}`;
};

/**
 * Main utility to sort the GLEs and then group them into an ORDERED ARRAY of groups,
 * preserving the sequence defined by the comparator.
 *
 * @param {Array<Object>} gles An array of GLE objects.
 * @param {string} defaultGradeLevel The current student's default grade (e.g., "K", "3", "5th").
 * @returns {Array<Object>} An ordered array of group objects: [{ key: '...', gles: [...] }, ...]
 */
const getOrderedGleGroups = (gles, defaultGradeLevel, gradeName) => {
    const defaultGradeNum = gradeStringToNumber(defaultGradeLevel);

    // 1. Sort the entire array using the complex priority logic.
    const sortedGles = gles.slice().sort((gleA, gleB) => {
        return gleComparatorDynamic(gleA, gleB, defaultGradeNum);
    });

    // 2. Iterate over the sorted array to create an ordered array of groups.
    return sortedGles.reduce((groups, gle) => {
        const currentKey = getUniqueGroupKey(gle, defaultGradeNum, gradeName);

        // Check if the last group in the array has the same key
        const lastGroup = groups.at(-1) ?? null;

        if (lastGroup?.key === currentKey) {
            // Append GLE to the last existing group (preserves internal order)
            lastGroup.gles.push(gle);
        } else {
            // Create a new group object and push it to the array (preserves external order)
            groups.push({
                key: currentKey,
                grade: getLowestDiffGradeNumber(gle), // Store grade for P3 display
                gles: [gle]
            });
        }
        return groups;
    }, []);
};

// Component for rendering the ratings on an Evidence Outcome
const EvidenceOutcomeRatings = ({ contentRating, diffRating }) => {
    return (
        <>
            {isParentFacingRating(diffRating?.rating) && <RatingBlock rating={getScrubbedRating(diffRating.rating)} />}
            {isParentFacingRating(contentRating?.rating) && (
                <RatingBlock rating={getScrubbedRating(contentRating.rating)} />
            )}
        </>
    );
};

EvidenceOutcomeRatings.propTypes = {
    contentRating: PropTypes.instanceOf(Object),
    diffRating: PropTypes.instanceOf(Object)
};

EvidenceOutcomeRatings.defaultProps = {
    contentRating: null,
    diffRating: null
};

const showEoTitle = (contentRating, diffRating) => {
    return (
        contentRating?.contentType !== "EVIDENCE_OUTCOME_CHILD" && diffRating?.contentType !== "EVIDENCE_OUTCOME_CHILD"
    );
};

// Component for rendering an Evidence Outcome
const EvidenceOutcomeRenderer = ({ eo }) => {
    const { contentRating, diffRating, eoBody, eocBody, evidenceOutcomeChildren } = eo;
    const hasOwnRating = isParentFacingRating(contentRating?.rating) || isParentFacingRating(diffRating?.rating);
    const ratedChildren = (evidenceOutcomeChildren || []).filter((child) => hasRatedElements([child]));
    const subjectEvidenceOutcome = T({ key: "Subject_Evidence_Outcome" });
    const subjectEvidenceOutcomeChild = T({ key: "Subject_Evidence_Outcome_Child" });
    const subjectStudentComment = T({ key: "Subject_Student_Comment" });
    if (!hasOwnRating && ratedChildren.length === 0) {
        return null;
    }
    return (
        <MathJax hideUntilTypeset="first" tabIndex={-1}>
            {hasOwnRating && (
                <div className="eo-container">
                    <div className="eo-left">
                        {showEoTitle(contentRating, diffRating) && (
                            <div className="eo-title">{subjectEvidenceOutcome}:</div>
                        )}
                        <div className="eo-body" dangerouslySetInnerHTML={{ __html: eoBody || eocBody }} />
                        {diffRating?.comment && (
                            <div className="eo-student-comment">
                                <div className="eo-title">{subjectStudentComment}:</div>
                                <div className="eo-body">{diffRating.comment}</div>
                            </div>
                        )}
                        {contentRating?.comment && (
                            <div className="eo-student-comment">
                                <div className="eo-title">{subjectStudentComment}:</div>
                                <div className="eo-body">{contentRating.comment}</div>
                            </div>
                        )}
                    </div>
                    <div className="eo-right">
                        <EvidenceOutcomeRatings contentRating={contentRating} diffRating={diffRating} />
                    </div>
                </div>
            )}
            {ratedChildren.length > 0 && (
                <div className="eo-children-container">
                    <div className="eo-child-title">{subjectEvidenceOutcomeChild}:</div>
                    {ratedChildren.map((childEo, index) => {
                        const uniqueKey = `child-eo-${index}${childEo.eoId ?? childEo.eocId}`;
                        return <EvidenceOutcomeRenderer key={uniqueKey} eo={childEo} />;
                    })}
                </div>
            )}
        </MathJax>
    );
};

EvidenceOutcomeRenderer.propTypes = {
    eo: PropTypes.instanceOf(Object).isRequired
};

// Component for rendering a Grade Level Expectation (GLE)
const GLERenderer = ({ gle }) => {
    const { diffRating: gleDiffRating, evidenceOutcomes, gleBody } = gle;
    const subjectStudentComment = T({ key: "Subject_Student_Comment" });

    // Partition EOs: those with no body are GLE-level ratings; those with a body are real EOs
    const gleRatingEo = getGleRatingEo(gle);
    const realEos = evidenceOutcomes?.filter((eo) => Boolean(eo.eoBody)) ?? [];

    const gleDirectRating = gleHasDirectRating(gle) ? getGleDirectRating(gle) : (gleRatingEo?.contentRating ?? null);
    const gleDirectDiffRating = gleDiffRating ?? gleRatingEo?.diffRating ?? null;
    const hasGleRating = isParentFacingRating(gleDirectRating?.rating);
    const hasGleDiffRating = isParentFacingRating(gleDirectDiffRating?.rating);

    const hasRealEoRatings = hasRatedElements(realEos);

    if (!hasGleRating && !hasGleDiffRating && !hasRealEoRatings) {
        return null;
    }

    return (
        <div className="gle-container">
            <div className="gle-title-row">
                <div className="gle-title">{gleBody ? `GLE: ${gleBody}` : "GLE"}</div>
                {(hasGleRating || hasGleDiffRating) && (
                    <div className="gle-rating">
                        {hasGleDiffRating && <RatingBlock rating={getScrubbedRating(gleDirectDiffRating.rating)} />}
                        {hasGleRating && <RatingBlock rating={getScrubbedRating(gleDirectRating.rating)} />}
                    </div>
                )}
            </div>
            {hasGleDiffRating && gleDirectDiffRating?.comment && (
                <div className="eo-student-comment">
                    <div className="eo-title">{subjectStudentComment}:</div>
                    <div className="eo-body">{gleDirectDiffRating.comment}</div>
                </div>
            )}
            {hasGleRating && gleDirectRating?.comment && (
                <div className="eo-student-comment">
                    <div className="eo-title">{subjectStudentComment}:</div>
                    <div className="eo-body">{gleDirectRating.comment}</div>
                </div>
            )}
            {realEos.map((eo, index) => {
                if (!hasRatedElements([eo])) {
                    return null;
                }
                return (
                    <Fragment key={`eo-${eo.eoId}-${index}`}>
                        <div className="eo-parent-container">
                            <EvidenceOutcomeRenderer eo={eo} />
                        </div>
                    </Fragment>
                );
            })}
        </div>
    );
};

GLERenderer.propTypes = {
    gle: PropTypes.instanceOf(Object).isRequired
};

// Component for rendering a CDE Standard
const StandardRenderer = ({ gradeName, hasDiffItems, standard }) => {
    const { gles } = standard;

    const grade = Number.parseInt(gradeName, 10) || "K";
    const groupedGles = getOrderedGleGroups(gles, grade, gradeName);
    const subjectAccelerated = T({ key: "Subject_Accelerated" });

    return groupedGles.map((obj, idx) => {
        const { gles: groupGles, key } = obj;
        const isRated = groupGles.some(gleGroupIsRated);

        if (!isRated) {
            return null;
        }

        const uniqueKey = `gle-collection-${key}-${idx}`;
        const className = key.includes(subjectAccelerated) ? "grade-container" : "same-grade-container";

        return (
            <Fragment key={uniqueKey}>
                {hasDiffItems ? (
                    <div className={className}>
                        <div className="grade-title">{key}</div>
                        {groupGles.map((gle, index) => (
                            <GLERenderer gle={gle} key={`gle-${gle.gleId}-${index}`} />
                        ))}
                    </div>
                ) : (
                    <>
                        {groupGles.map((gle, index) => (
                            <GLERenderer gle={gle} key={`gle-${gle.gleId}-${index}`} />
                        ))}
                    </>
                )}
            </Fragment>
        );
    });
};

StandardRenderer.propTypes = {
    gradeName: PropTypes.string.isRequired,
    hasDiffItems: PropTypes.bool.isRequired,
    standard: PropTypes.instanceOf(Object)
};

StandardRenderer.defaultProps = {
    standard: {}
};

// Component for rendering a single standard accordion item
const StandardAccordionItem = ({ gradeName, hasDiffItems, index, standard, subjectName }) => {
    const uniqueKey = `${gradeName}-${standard.cdestandardBody}-${index}`;
    const hasRatings = standardHasRatedElements(standard);
    const hasLabel = Boolean(standard.cdestandardBody) || (standard.gles || []).some((gle) => gle.gleBody);
    if (!hasRatings || !hasLabel) {
        return null;
    }
    return (
        <Accordion.Item className="dt-std-accordion-item" eventKey={`${subjectName}-${index}`} key={uniqueKey}>
            <Accordion.Header className="reportable-content-wrapper-accordion" data-bs-toggle="collapse">
                <div className="rc-accordion-title">
                    <div className="selectable-content-label">
                        <div className="selectable-input-wrapper">
                            <p className="guardian-content-title">Standard: {standard.cdestandardBody}</p>
                        </div>
                    </div>
                </div>
            </Accordion.Header>
            <Accordion.Body className="reportable-content-wrapper-accordion-body">
                <StandardRenderer
                    gradeName={gradeName}
                    hasDiffItems={hasDiffItems}
                    key={`standard-${standard.cdestandardId}-${index}`}
                    standard={standard}
                />
            </Accordion.Body>
        </Accordion.Item>
    );
};

StandardAccordionItem.propTypes = {
    gradeName: PropTypes.string.isRequired,
    hasDiffItems: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
    standard: PropTypes.instanceOf(Object).isRequired,
    subjectName: PropTypes.string.isRequired
};

// Main Component
export const ConsolidatedStandardsReport = ({
    gradeName,
    leadingContent,
    rawData,
    subjectName,
    reportableContent = null,
    diffRatings = []
}) => {
    const mergedStandards = useMemo(
        () =>
            mergeSubjectStandards(rawData, subjectName, { reportableContent, diffRatings }).sort((a, b) =>
                compareText(a.cdestandardBody, b.cdestandardBody)
            ),
        [diffRatings, rawData, reportableContent, subjectName]
    );

    const hasDiffItems = getHasDiffItems(mergedStandards);
    const hasRatings = mergedStandardsHaveParentFacingRatings(mergedStandards);
    const hasCustomData = subjectHasParentFacingCustomData(rawData, subjectName);
    // customData-only subjects (e.g. fluency WPM with no content ratings) still show.
    if (!hasRatings && !hasCustomData) {
        return null;
    }

    return (
        <div className="subject-container">
            <div className="subject-title">{subjectName} Ratings</div>
            <div className="subject-message">
                {leadingContent}
                {hasRatings && mergedStandards.length > 0 ? (
                    <Accordion>
                        {mergedStandards.map((standard, index) => (
                            <StandardAccordionItem
                                key={`${gradeName}-${standard.cdestandardBody}-${index}`}
                                gradeName={gradeName}
                                hasDiffItems={hasDiffItems}
                                index={index}
                                standard={standard}
                                subjectName={subjectName}
                            />
                        ))}
                    </Accordion>
                ) : null}
                {hasRatings && mergedStandards.length === 0 ? <p>No standards found for {subjectName}.</p> : null}
            </div>
        </div>
    );
};

ConsolidatedStandardsReport.propTypes = {
    gradeName: PropTypes.string.isRequired,
    leadingContent: PropTypes.node,
    rawData: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
    subjectName: PropTypes.string.isRequired,
    reportableContent: PropTypes.instanceOf(Object),
    diffRatings: PropTypes.arrayOf(PropTypes.instanceOf(Object))
};

ConsolidatedStandardsReport.defaultProps = {
    leadingContent: null,
    reportableContent: null,
    diffRatings: []
};
